import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import {
  LuBox, LuBriefcase, LuCheckCircle2, LuClock, LuExternalLink, LuGlobe, LuHash, LuInfo, LuListTree,
  LuMonitorSmartphone, LuUser, LuVenetianMask, LuXCircle, LuGitCompare, LuFileText, LuLink,
} from 'react-icons/lu';
import { Drawer, LoadingSpinner, PersonChip } from 'components/common';
import { fetchAuditLog } from './auditLogApi';
import { categoryLabel, deviceLabel, eventLabel } from './auditLogLabels';
import { eventIcon, iconTileClass } from './auditLogIcons';
import { CopyValue, DetailRow, Section } from './AuditLogDetailParts';

const show = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

// "Crm::Project::Waiver" → "Waiver"; the full name stays in the tooltip.
const shortType = (type) => String(type || '').split('::').pop();

const OutcomePill = ({ outcome }) => (outcome === 'failure' ? (
  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-red-50 tw-text-red-700">
    <LuXCircle size={12} /> Failed / refused
  </span>
) : (
  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-emerald-50 tw-text-emerald-700">
    <LuCheckCircle2 size={12} /> Succeeded
  </span>
));

OutcomePill.propTypes = { outcome: PropTypes.string };
OutcomePill.defaultProps = { outcome: 'success' };

const EventHeader = ({ detail }) => {
  const Icon = eventIcon(detail.action, detail.category);
  const when = dayjs(detail.created_at);
  return (
    <div className="tw-flex tw-items-start tw-gap-3.5">
      <span className={`tw-inline-flex tw-items-center tw-justify-center tw-w-11 tw-h-11 tw-rounded-xl tw-shrink-0 ${iconTileClass(detail.outcome)}`}>
        <Icon size={20} aria-hidden="true" />
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <h3 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-slate-900 tw-leading-6">{eventLabel(detail.action)}</h3>
        <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-slate-100 tw-text-slate-700">{categoryLabel(detail.category)}</span>
          <OutcomePill outcome={detail.outcome} />
          <code className="tw-font-mono tw-text-[11px] tw-text-slate-400">{detail.action}</code>
        </div>
        <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500">
          <LuClock size={12} aria-hidden="true" />
          <span title={when.format('DD-MMM-YYYY HH:mm:ss Z')}>{when.format('ddd, DD MMM YYYY · HH:mm:ss')}</span>
        </div>
      </div>
    </div>
  );
};

EventHeader.propTypes = { detail: PropTypes.object.isRequired };

// audited_changes is { field: [before, after] } for business rows, and a flat
// object for some older rows — both render as before → after.
const ChangesTable = ({ changes }) => {
  const entries = Object.entries(changes || {});
  if (entries.length === 0) return null;

  return (
    <Section title="Changes" icon={LuGitCompare}>
      <div className="tw-divide-y tw-divide-slate-100">
        {entries.map(([field, value]) => {
          const [before, after] = Array.isArray(value) && value.length === 2 ? value : [undefined, value];
          return (
            <div key={field} className="tw-py-2.5">
              <div className="tw-text-xs tw-font-semibold tw-text-slate-600 tw-mb-1">{field}</div>
              <div className="tw-grid tw-grid-cols-[1fr_auto_1fr] tw-items-start tw-gap-2 tw-text-sm">
                <span className="tw-break-all tw-text-slate-500 tw-line-through tw-decoration-slate-300">{show(before)}</span>
                <span className="tw-text-slate-300">→</span>
                <span className="tw-break-all tw-text-slate-900">{show(after)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

ChangesTable.propTypes = { changes: PropTypes.object };
ChangesTable.defaultProps = { changes: null };

const AuditLogDetailDrawer = ({ auditId, onHide }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!auditId) return undefined;
    let active = true;
    setDetail(null);
    setError('');
    fetchAuditLog(auditId)
      .then((data) => { if (active) setDetail(data); })
      .catch(() => { if (active) setError('Could not load this event.'); });
    return () => { active = false; };
  }, [auditId]);

  const hasMetadata = detail?.metadata && Object.keys(detail.metadata).length > 0;

  return (
    <Drawer show={!!auditId} onHide={onHide} title="Audit event" width={640}>
      {error && <p className="tw-text-sm tw-text-red-600">{error}</p>}
      {!error && !detail && <LoadingSpinner />}
      {detail && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <EventHeader detail={detail} />

          {detail.comment && (
            <div className="tw-flex tw-gap-2.5 tw-p-3.5 tw-rounded-xl tw-bg-slate-50 tw-text-sm tw-text-slate-700">
              <LuInfo size={16} className="tw-mt-0.5 tw-shrink-0 tw-text-slate-400" aria-hidden="true" />
              <p className="tw-m-0 tw-whitespace-pre-line">{detail.comment}</p>
            </div>
          )}

          <Section title="Who" icon={LuUser}>
            <DetailRow icon={LuUser} label="User">
              {detail.user
                ? <PersonChip name={detail.user.name} fullName={detail.user.full_name} email={detail.user.email} size="sm" />
                : detail.username}
            </DetailRow>
            {detail.impersonator && (
              <DetailRow icon={LuVenetianMask} label="Viewed as by">{detail.impersonator.name}</DetailRow>
            )}
          </Section>

          <Section title="What" icon={LuBox}>
            <DetailRow icon={LuFileText} label="Record">
              {detail.target_type && (
                <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-1.5" title={detail.target_type}>
                  <span>{shortType(detail.target_type)}</span>
                  {detail.target_id && <CopyValue value={String(detail.target_id)} />}
                </span>
              )}
            </DetailRow>
            <DetailRow icon={LuBriefcase} label="Project">
              {detail.project && (
                <Link
                  to={`/crm/projects/${detail.project.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-text-primary-600 hover:tw-underline"
                >
                  {detail.project.name || `#${detail.project.id}`}
                  <LuExternalLink size={12} />
                </Link>
              )}
            </DetailRow>
          </Section>

          <Section title="Where" icon={LuGlobe}>
            <DetailRow icon={LuGlobe} label="IP address">{detail.remote_address}</DetailRow>
            <DetailRow icon={LuMonitorSmartphone} label="Device">
              {detail.user_agent && (
                <>
                  <div>{deviceLabel(detail.user_agent)}</div>
                  <div className="tw-text-xs tw-text-slate-400 tw-break-all">{detail.user_agent}</div>
                </>
              )}
            </DetailRow>
            <DetailRow icon={LuHash} label="Request">
              {detail.request_uuid && <CopyValue value={detail.request_uuid} />}
            </DetailRow>
          </Section>

          <ChangesTable changes={detail.changes} />

          {hasMetadata && (
            <Section title="Details" icon={LuListTree}>
              <pre className="tw-text-xs tw-text-slate-700 tw-bg-slate-50 tw-p-3 tw-my-2 tw-rounded-lg tw-overflow-x-auto">
                {JSON.stringify(detail.metadata, null, 2)}
              </pre>
            </Section>
          )}

          {detail.related?.length > 0 && (
            <Section title={`Same request (${detail.related.length})`} icon={LuLink}>
              {detail.related.map((r) => {
                const Icon = eventIcon(r.action, r.category);
                return (
                  <div key={r.id} className="tw-flex tw-items-start tw-gap-2.5 tw-py-2 tw-text-sm tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 last:tw-border-b-0">
                    <Icon size={14} className="tw-mt-0.5 tw-shrink-0 tw-text-slate-400" aria-hidden="true" />
                    <span><span className="tw-font-medium tw-text-slate-800">{eventLabel(r.action)}</span> — {r.comment}</span>
                  </div>
                );
              })}
            </Section>
          )}
        </div>
      )}
    </Drawer>
  );
};

AuditLogDetailDrawer.propTypes = {
  auditId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onHide: PropTypes.func.isRequired,
};

AuditLogDetailDrawer.defaultProps = { auditId: null };

export default AuditLogDetailDrawer;
